import { message, Space, Row, Col, Descriptions, DatePicker, Popconfirm, Button, Typography } from 'antd';
import { useState } from 'react';
import { useAuth } from 'react-oidc-context';
import dayjs from 'dayjs';
import { PageHeader } from '@ant-design/pro-layout';
import { FormatDate } from '../../../../constants';

const { Title } = Typography;

interface FinanceToolsProps {
  // TODO: add query for displaying counts for statuses of active gl posting summary records
  glPostingSummaryData: any;
}

export const FinanceTools: React.FC<FinanceToolsProps> = (_props) => {
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const auth = useAuth();

  const handleResend = () => {
    if (!startDate) {
      message.error('Please select at least start date');
      return;
    }
    const simpleStartDate = FormatDate(startDate, 'YYYY-MM-DD');
    const simpleEndDate = endDate !== null ? FormatDate(endDate, 'YYYY-MM-DD') : undefined;

    console.log('Resending batch for ', simpleStartDate, ' to ', simpleEndDate);

    const uri = `${import.meta.env.VITE_FINANCE_BATCH_API_ENDPOINT}`;
    setLoading(true);
    fetch(uri, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth?.user?.access_token}`
      },
      body: JSON.stringify({
        startDate: simpleStartDate,
        endDate: simpleEndDate
      })
    })
      .then((data) => {
        if (data.status === 200) {
          message.success('Batch resent successfully');
        } else {
          message.error('Failed to resend batch');
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        message.error('An error occurred while resending the batch');
      });
    setLoading(false);
  };

  const handleReprocess = () => {
    if (!startDate) {
      message.error('Please select at least start date');
      return;
    }

    const simpleStartDate = FormatDate(startDate, 'YYYY-MM-DD');
    const simpleEndDate = endDate !== null ? FormatDate(endDate, 'YYYY-MM-DD') : undefined;

    console.log('Reprocessing batch for ', simpleStartDate, ' to ', simpleEndDate);

    const uri = `${import.meta.env.VITE_FINANCE_BATCH_API_ENDPOINT}`;

    fetch(uri, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth?.user?.access_token}`
      },
      body: JSON.stringify({
        startDate: simpleStartDate,
        endDate: simpleEndDate,
        reprocessBatch: true
      })
    })
      .then((data) => {
        if (data.status === 200) {
          message.success('Batch reprocess successfully');
        } else {
          message.error('Failed to reprocess batch');
        }
      })
      .catch((error) => {
        console.error('Error:', error);
        message.error('An error occurred while reprocessing the batch');
      });
  };

  return (
    <>
      <PageHeader>
        <div id="leftTitle" >
          <Title level={3} style={{ display: 'inline' }}>
            Finance Tools
          </Title>
          &nbsp;&nbsp;&nbsp;&nbsp;
        </div>
      </PageHeader>
      <div style={{ margin: '25px 20px 200px' }}>
        <div style={{ padding: '10px 0px', marginBottom: '20px' }}>
          <Title level={5} >
            Manual Trigger for Finance Batches
          </Title>
          <Descriptions.Item>Enter a date range to reattempt for multiple finance batches or just provide a start date for one batch individually.</Descriptions.Item>
          <br/>
          <Descriptions.Item><strong>Note:</strong> Reprocess assumes the original posting summary and transaction summary records from that gl transaction date are removed from cosmos/blob.</Descriptions.Item>
        </div>
        <Space direction="vertical" size="large" style={{ minWidth: '500px' }}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Descriptions title="Start Date" />
              <DatePicker value={startDate} onChange={(date) => setStartDate(date)} maxDate={dayjs(endDate) ?? undefined} />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Descriptions title="End Date" />
              <DatePicker value={endDate} onChange={(date) => setEndDate(date)} minDate={dayjs(startDate) ?? undefined} />
            </Col>
          </Row>
          <Row gutter={16} style={{ alignContent: 'left' }}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Popconfirm title="Are you sure you want to resend the batch?" onConfirm={handleResend}>
                <Button type="primary" loading={loading} >Resend</Button>
              </Popconfirm>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Popconfirm title="Are you sure you want to reprocess the batch?" onConfirm={handleReprocess}>
                <Button type="primary" loading={loading}>Reprocess</Button>
              </Popconfirm>
            </Col>
          </Row>
        </Space>
      </div>
    </>
  );
};
